import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    User,
    Trophy,
    GraduationCap,
    BookOpen,
    Calendar,
    Image,
    FileText,
    MessageSquare,
    TrendingUp,
    LogOut,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { toast } from "sonner";

const AdminNavbar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const navItems = [
        {
            path: "/admin",
            icon: LayoutDashboard,
            label: "Dashboard",
        },
        {
            path: "/admin/analytics",
            icon: TrendingUp,
            label: "Traffic & Pengunjung",
        },
        {
            path: "/admin/profile",
            icon: User,
            label: "Profil",
        },
        {
            path: "/admin/achievements",
            icon: Trophy,
            label: "Pencapaian",
        },
        {
            path: "/admin/education",
            icon: GraduationCap,
            label: "Pendidikan",
        },
        {
            path: "/admin/courses",
            icon: Calendar,
            label: "Kegiatan Kursus",
        },
        {
            path: "/admin/certificates",
            icon: BookOpen,
            label: "Sertifikat",
        },
        {
            path: "/admin/gallery",
            icon: Image,
            label: "Galeri",
        },
        {
            path: "/admin/worksheets",
            icon: FileText,
            label: "Worksheet",
        },
        {
            path: "/admin/contact",
            icon: MessageSquare,
            label: "Kontak",
        },
    ];

    const handleLogout = async () => {
        try {
            const { error } = await supabase.auth.signOut();
            if (error) throw error;

            toast.success("Berhasil logout");
            navigate("/admin/login");
        } catch (error) {
            console.error("Error logging out:", error);
            toast.error("Gagal logout");
        }
    };

    const isActive = (path: string) => {
        if (path === "/admin") {
            return (
                location.pathname === "/admin" ||
                location.pathname === "/admin/"
            );
        }
        return location.pathname.startsWith(path);
    };

    return (
        <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo/Brand */}
                    <div className="flex-shrink-0">
                        <h1 className="text-xl font-bold text-gray-900">
                            Admin Panel
                        </h1>
                    </div>

                    {/* Navigation Items */}
                    <div className="hidden lg:block flex-1 max-w-4xl">
                        <div className="ml-6 flex items-center justify-center flex-wrap gap-2">
                            {navItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`px-2 py-1.5 rounded-md text-xs font-medium flex items-center space-x-1 transition-colors whitespace-nowrap ${
                                            isActive(item.path)
                                                ? "bg-blue-100 text-blue-700"
                                                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                        }`}
                                    >
                                        <Icon className="w-3.5 h-3.5" />
                                        <span className="hidden xl:inline">
                                            {item.label}
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    {/* Logout Button */}
                    <div className="flex items-center">
                        <button
                            onClick={handleLogout}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span>Keluar</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <div className="lg:hidden">
                <div className="px-2 pt-2 pb-3 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-1">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`px-2 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors ${
                                        isActive(item.path)
                                            ? "bg-blue-100 text-blue-700"
                                            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span className="truncate">
                                        {item.label}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default AdminNavbar;
